// Local backup of PHD Monthly Cost Status narrative fields.
//
// Why this exists: the form holds 8 ReactQuill rich-text fields in Redux
// only. If the modal closes, the page refreshes, the browser crashes, or
// the session times out before the user clicks Save, all that text is
// lost. PMs have repeatedly lost work this way. This module
// mirrors those fields to localStorage on every change so they can be
// restored on the next form open.
//
// Scope: only the 8 narrative fields. Files, snapshot selection, and
// status are not persisted here — they require explicit user action and
// are not the source of the data loss complaints.
//
// Two normalization modes exist:
//   - "storage" form: all 8 keys, each a string (possibly empty). This
//     preserves user intent — a field explicitly cleared by the user
//     must round-trip as an empty string, not be dropped, otherwise
//     the restore merge would resurrect the server's old text.
//   - "compare" form: empty Quill markup ("<p><br></p>", "&nbsp;",
//     whitespace) collapses to "". Used for dirty-checking so that a
//     freshly hydrated form (Redux full of "") equals an empty server
//     response ({}).

const KEY_PREFIX = 'phd-cost-status-draft-v1';

export const PHD_NARRATIVE_FIELD_IDS = [
	'financial_summary',
	'billing_summary',
	'permitting_and_design',
	'buyout_progress',
	'construction_progress',
	'project_risk',
	'raving_fans',
	'additional_comments',
];

// A real month is required: the form is always opened in the context of
// a specific cost status month, and a "current" fallback would race with
// hydration (start typing before server data arrives → save under a
// temporary key → never restored because hydration looks up the real
// month). If month isn't known yet, this returns null and save/load
// no-op silently.
export function getDraftStorageKey({ projectId, month }) {
	if (projectId === null || projectId === undefined || projectId === '') return null;
	if (!month) return null;
	return `${KEY_PREFIX}:${projectId}:${month}`;
}

function safeStorage() {
	try {
		if (typeof window === 'undefined') return null;
		return window.localStorage || null;
	} catch (e) {
		return null;
	}
}

// True if `value` contains any real text after stripping HTML, entities,
// and whitespace. ReactQuill emits "<p><br></p>" for an empty editor.
function isNonEmptyHtml(value) {
	if (typeof value !== 'string') return false;
	const stripped = value
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, '')
		.replace(/\s|\u00a0/g, '');
	return stripped.length > 0;
}

// Storage form — always returns all 8 keys, preserving empty strings.
// Use this when writing to localStorage and when overlaying a draft
// back onto a form.
export function narrativeFieldsForStorage(formData) {
	const out = {};
	for (const id of PHD_NARRATIVE_FIELD_IDS) {
		const v = formData?.[id];
		out[id] = typeof v === 'string' ? v : '';
	}
	return out;
}

// Compare form — same 8 keys, but empty Quill markup collapses to "".
// Use this when checking whether two field objects should be
// considered equivalent.
export function narrativeFieldsForCompare(formData) {
	const out = {};
	for (const id of PHD_NARRATIVE_FIELD_IDS) {
		const v = formData?.[id];
		out[id] = isNonEmptyHtml(v) ? v : '';
	}
	return out;
}

// Both sides are normalized identically before stringify, and the 8
// keys are always inserted in PHD_NARRATIVE_FIELD_IDS order, so key
// ordering is stable across invocations.
export function narrativeFieldsEqual(a, b) {
	return JSON.stringify(narrativeFieldsForCompare(a))
		=== JSON.stringify(narrativeFieldsForCompare(b));
}

// Any narrative field has real content.
export function hasMeaningfulContent(fields) {
	if (!fields || typeof fields !== 'object') return false;
	for (const id of PHD_NARRATIVE_FIELD_IDS) {
		if (isNonEmptyHtml(fields[id])) return true;
	}
	return false;
}

export function saveDraft({ projectId, month, fields }) {
	const storage = safeStorage();
	const key = getDraftStorageKey({ projectId, month });
	if (!storage || !key) return false;
	try {
		const payload = {
			savedAt: Date.now(),
			fields: narrativeFieldsForStorage(fields),
		};
		storage.setItem(key, JSON.stringify(payload));
		return true;
	} catch (e) {
		return false;
	}
}

export function loadDraft({ projectId, month }) {
	const storage = safeStorage();
	const key = getDraftStorageKey({ projectId, month });
	if (!storage || !key) return null;
	try {
		const raw = storage.getItem(key);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object') return null;
		if (typeof parsed.savedAt !== 'number') return null;
		return {
			savedAt: parsed.savedAt,
			fields: narrativeFieldsForStorage(parsed.fields),
		};
	} catch (e) {
		return null;
	}
}

export function clearDraft({ projectId, month }) {
	const storage = safeStorage();
	const key = getDraftStorageKey({ projectId, month });
	if (!storage || !key) return false;
	try {
		storage.removeItem(key);
		return true;
	} catch (e) {
		return false;
	}
}

// Should we overlay the local draft onto the server-hydrated form?
//
// Three conditions must hold:
//   1. A local draft exists and has a valid timestamp.
//   2. Its fields differ from the server's (otherwise there is nothing
//      to restore — covers the "user typed and reverted" case).
//   3. It is strictly newer than the server's last save (otherwise the
//      server is more up-to-date and should win).
//
// Fail-open: if the server timestamp is missing or unparseable, we
// treat the local draft as authoritative. This is the safer direction
// for a data-loss feature: a PM who just submitted will see a
// restored draft (one-time annoyance) rather than losing unsubmitted
// work permanently.
export function shouldRestoreLocalDraft({ localDraft, serverFields, serverLastUpdatedAt }) {
	if (!localDraft || typeof localDraft.savedAt !== 'number') return false;
	if (narrativeFieldsEqual(localDraft.fields, serverFields)) return false;
	if (!serverLastUpdatedAt) return true;
	const serverMs = new Date(serverLastUpdatedAt).getTime();
	if (Number.isNaN(serverMs)) return true;
	return localDraft.savedAt > serverMs;
}
